/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpRequest, HttpResponse } from '@angular/common/http';
import { getOpenTelemetryProviderAsync, OpenTelemetryProviderLike } from './open-telemetry.facade';
import { OtelcolConfig } from './config/open-telemetry.interfaces';
import { OpenTelemetryConfigFactory } from './config/open-telemetry.config.factory';
import { inject, Injectable } from '@angular/core';
import { OpenTelemetryService } from './open-telemetry.service';

// Интерфейс для кастомного контекста
export interface CustomSpanContext {
  traceId: string;
  spanId: string;
  traceFlags: number;
  isRemote: boolean;
}

export const TraceFlags: number = 0x01;

// Получение текущего времени в наносекундах для OpenTelemetry
export function getCurrentNanoseconds(): number {
  return (performance?.timeOrigin || 0) + (performance?.now?.() || 0);
}

enum OTHeaderParams {
  HttpStatusCode = 'http.status_code',
  HttpMethod = 'http.method',
  HttpUrl = 'http.url',
  HttpTarget = 'http.target',
  HttpHost = 'http.host',
  HttpUserAgent = 'http.user_agent',
}

// Кастомный генератор ID
export class CustomIdGenerator {
  private static readonly TRACE_ID_LENGTH: number = 32;
  private static readonly SPAN_ID_LENGTH: number = 16;

  generateTraceId(): string {
    return this.generateRandomHex(CustomIdGenerator.TRACE_ID_LENGTH / 2);
  }

  generateSpanId(): string {
    return this.generateRandomHex(CustomIdGenerator.SPAN_ID_LENGTH / 2);
  }

  private generateRandomHex(bytes: number): string {
    const array: Uint8Array<ArrayBuffer> = new Uint8Array(bytes);
    if (typeof window !== 'undefined' && window.crypto) {
      window.crypto.getRandomValues(array);
    } else {
      for (let i: number = 0; i < bytes; i++) {
        array[i] = Math.floor(Math.random() * 256);
      }
    }
    return Array.from(array, (byte: number) => byte.toString(16).padStart(2, '0')).join('');
  }
}

// Генерация traceparent заголовка
export function getTraceParent(spanContext: CustomSpanContext): string {
  const version: string = '00';
  const traceId: string = spanContext.traceId;
  const parentId: string = spanContext.spanId;
  const traceFlags: string = spanContext.traceFlags.toString(16).padStart(2, '0');
  return `${version}-${traceId}-${parentId}-${traceFlags}`;
}

@Injectable({ providedIn: 'root' })
export class OpenTelemetrySpanService {
  private openTelemetryConfigFactory: OpenTelemetryConfigFactory = inject(OpenTelemetryConfigFactory);
  private openTelemetryService: OpenTelemetryService = inject(OpenTelemetryService);

  public async processSpanAsync(
    type: 'response' | 'error',
    data: HttpResponse<any>,
    originalRequest: HttpRequest<any>,
    spanContext: CustomSpanContext,
    startTimeNs: number,
    endTimeNs: number
  ): Promise<void> {
    await this.openTelemetryService.ensureInitialized();
    const provider: any = await getOpenTelemetryProviderAsync();

    if (!provider) {
      return;
    }

    let span: any | undefined;

    try {
      span = this.initializeSpan(provider, startTimeNs);

      replaceSpanContext(span, spanContext);
      setRequestAttributes(span, originalRequest);

      switch (type) {
        case 'response':
          const response: HttpResponse<any> = data;
          span.setAttribute(OTHeaderParams.HttpStatusCode, response.status);
          span.setStatus({ code: 1, message: `HTTP ${response.status}` });
          span.addEvent?.('response.received', { [OTHeaderParams.HttpStatusCode]: response.status });
          break;

        case 'error':
          const error: any = data;
          span.recordException?.({
            name: error.name || 'HTTPError',
            message: error.message || 'Unknown error',
            stack: error.error,
          });
          span.setAttribute(OTHeaderParams.HttpStatusCode, error.status || 0);
          span.setStatus({ code: 2, message: error.message || 'HTTP Request Failed' });
          break;
      }
    } catch (error) {
      console.warn(`OpenTelemetry ${type} processing failed:`, error);
    } finally {
      if (span) {
        finalizeSpan(span, endTimeNs);
      }
    }
  }

  private initializeSpan(provider: OpenTelemetryProviderLike, startTimeNs: number): any {
    if (!provider) {
      return null;
    }

    const otelConfig: OtelcolConfig = this.openTelemetryConfigFactory.get();

    const serviceName: string = otelConfig.commonConfig.serviceName;

    const tracer: any = provider.getTracer(serviceName);

    // Создаем спан
    const spanOptions: any = { kind: 2 };
    spanOptions.startTime = startTimeNs;

    const span: any = tracer.startSpan('http.request', spanOptions);

    span.startTimeUnixNano = startTimeNs;

    return span;
  }
}

// Замена traceId и spanId в спане на кастомные значения
function replaceSpanContext(span: any, context: CustomSpanContext): void {
  try {
    // Получаем текущий контекст спана
    const originalContext: any = span.spanContext?.();

    if (originalContext) {
      // Создаем новый контекст с кастомными значениями
      const newContext: any = {
        ...originalContext,
        traceId: context.traceId,
        spanId: context.spanId,
        traceFlags: context.traceFlags,
      };

      // Пытаемся обновить контекст спана
      if (span.updateContext && typeof span.updateContext === 'function') {
        span.updateContext(newContext);
      } else if (span._spanContext) {
        // Альтернативный способ для некоторых реализаций
        span._spanContext = newContext;
      } else if (span.context && typeof span.context === 'function') {
        // Еще один альтернативный способ
        span.context().traceId = context.traceId;
        span.context().spanId = context.spanId;
        span.context().traceFlags = context.traceFlags;
      }
    }
  } catch (error) {
    console.warn('Failed to replace span context:', error);
    // Если не удалось заменить контекст, хотя бы установим атрибуты
    span.setAttribute('trace_id', context.traceId);
    span.setAttribute('span_id', context.spanId);
  }
}

// Установка атрибутов запроса
function setRequestAttributes(span: any, request: HttpRequest<any>): void {
  if (span.setAttributes && typeof span.setAttributes === 'function') {
    span.setAttributes({
      [OTHeaderParams.HttpMethod]: request.method,
      [OTHeaderParams.HttpUrl]: request.urlWithParams,
      [OTHeaderParams.HttpTarget]: request.urlWithParams,
      [OTHeaderParams.HttpHost]: request.headers.get('Host') || window.location.hostname,
      [OTHeaderParams.HttpUserAgent]: request.headers.get('User-Agent') || navigator.userAgent,
    });
  }
}

// Финализация спана
function finalizeSpan(span: any, endTimeNs: number): void {
  if (!span || span.ended) return;

  try {
    // Устанавливаем точное время окончания в наносекундах Unix
    span.endTimeUnixNano = endTimeNs;

    if (typeof span.end === 'function') {
      span.end(endTimeNs);
    }
  } catch (e) {
    console.warn('Error finalizing span:', e);
  }
}
